import { LitElement, html } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { styleMap } from "lit/directives/style-map.js";
import { customElement, state } from "lit/decorators.js";
import { api } from "../api/client";
import { i18n } from "../i18n";
import "../components/app-dialog";
import "../components/login-wallpaper";

@customElement("login-view")
export class LoginView extends LitElement {
  @state() private activeSurface: "login" | "display" = "login";
  @state() private setupRequired = false;
  @state() private setupLoading = true;
  @state() private error = "";
  @state() private busy = false;
  @state() private registerOpen = false;
  @state() private registerBusy = false;
  @state() private registerError = "";
  @state() private registerSuccess = "";

  @state() private windowX = 0;
  @state() private windowY = 0;
  @state() private draggingWindow = false;
  @state() private trails: {
    id: number;
    left: number;
    top: number;
    width: number;
    height: number;
  }[] = [];
  private drag?: {
    pointer: number;
    x: number;
    y: number;
    left: number;
    top: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    target: HTMLElement;
  };
  private trailId = 0;
  private lastTrail = 0;
  private trailTimers = new Set<ReturnType<typeof setTimeout>>();

  private onTitle(event: Event) {
    return event
      .composedPath()
      .some((node) => node instanceof HTMLElement && node.id === "draggable");
  }

  private beginWindowDrag = (event: PointerEvent) => {
    if (event.button !== 0 || !this.onTitle(event) || this.registerOpen) return;
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    event.preventDefault();
    this.drag = {
      pointer: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      offsetX: this.windowX,
      offsetY: this.windowY,
      width: rect.width,
      height: rect.height,
      target,
    };
    target.setPointerCapture(event.pointerId);
    this.draggingWindow = true;
    this.lastTrail = 0;
  };

  private moveWindow = (event: PointerEvent) => {
    const drag = this.drag;
    if (!drag || drag.pointer !== event.pointerId) return;
    const left = Math.max(
      0,
      Math.min(
        window.innerWidth - drag.width,
        drag.left + event.clientX - drag.x,
      ),
    );
    const top = Math.max(
      0,
      Math.min(
        window.innerHeight - drag.height,
        drag.top + event.clientY - drag.y,
      ),
    );
    const nextX = drag.offsetX + left - drag.left;
    const nextY = drag.offsetY + top - drag.top;
    if (Math.abs(nextX - this.windowX) + Math.abs(nextY - this.windowY) < 2)
      return;
    if (
      event.timeStamp - this.lastTrail >= 35 &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      const rect = drag.target.getBoundingClientRect();
      const id = ++this.trailId;
      this.trails = [
        ...this.trails.slice(-9),
        {
          id,
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
      ];
      this.lastTrail = event.timeStamp;
      const timer = setTimeout(() => {
        this.trails = this.trails.filter((trail) => trail.id !== id);
        this.trailTimers.delete(timer);
      }, 650);
      this.trailTimers.add(timer);
    }
    this.windowX = nextX;
    this.windowY = nextY;
  };

  private endWindowDrag = () => {
    const drag = this.drag;
    this.drag = undefined;
    this.draggingWindow = false;
    if (drag?.target.hasPointerCapture(drag.pointer))
      drag.target.releasePointerCapture(drag.pointer);
  };

  private resetWindow = () => {
    this.endWindowDrag();
    this.windowX = this.windowY = 0;
    this.trails = [];
  };

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    window.addEventListener("resize", this.resetWindow);
    window.addEventListener("blur", this.endWindowDrag);
    void this.loadSetup();
  }

  private async loadSetup() {
    this.setupLoading = true;
    try {
      this.setupRequired = (await api.setup()).required;
      return true;
    } catch (error) {
      this.error = (error as Error).message;
      return false;
    } finally {
      this.setupLoading = false;
    }
  }

  private openRegister = async () => {
    this.registerError = "";
    this.registerSuccess = "";
    if (await this.loadSetup()) this.registerOpen = true;
  };

  private languageChanged = () => this.requestUpdate();

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    window.removeEventListener("resize", this.resetWindow);
    window.removeEventListener("blur", this.endWindowDrag);
    this.endWindowDrag();
    this.trailTimers.forEach(clearTimeout);
    this.trailTimers.clear();
    super.disconnectedCallback();
  }

  private async submit(event: SubmitEvent) {
    event.preventDefault();
    if (this.busy) return;
    this.busy = true;
    this.error = "";
    const values = Object.fromEntries(
      new FormData(event.currentTarget as HTMLFormElement),
    );
    try {
      const data = await api.login(values);
      this.dispatchEvent(
        new CustomEvent("authenticated", { detail: data.user, bubbles: true }),
      );
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private async register(event: SubmitEvent) {
    event.preventDefault();
    this.registerBusy = true;
    this.registerError = "";
    const values = Object.fromEntries(
      new FormData(event.currentTarget as HTMLFormElement),
    );
    try {
      const result = await api.register(values);
      this.registerOpen = false;
      this.registerSuccess = i18n.t(
        result.pending ? "registerPending" : "setupSuccess",
      );
      if (!result.pending) this.setupRequired = false;
    } catch (error) {
      this.registerError = (error as Error).message;
    } finally {
      this.registerBusy = false;
    }
  }

  private confirmRegister = () => {
    this.querySelector<HTMLFormElement>("form.register-form")?.requestSubmit();
  };

  render() {
    return html`
      <a2k-desktop class="login-desktop">
        <login-wallpaper
          .active=${this.activeSurface === "display" && !this.registerOpen}
          @wallpaper-focus=${() => {
            this.activeSurface = "display";
          }}
        ></login-wallpaper>
        <div class="login-drag-trails" aria-hidden="true">
          ${repeat(
            this.trails,
            (trail) => trail.id,
            (trail) =>
              html`<div
                class="login-drag-trail"
                style=${styleMap({
                  left: `${trail.left}px`,
                  top: `${trail.top}px`,
                  width: `${trail.width}px`,
                  height: `${trail.height}px`,
                })}
              >
                <div class="login-drag-trail-title">
                  ${i18n.t("loginHeading")}
                </div>
                <div class="login-drag-trail-body">
                  <span class="brand-mark">CJ</span>
                  <strong>${i18n.t("loginTitle")}</strong>
                  <div></div>
                  <div></div>
                </div>
              </div>`,
          )}
        </div>
        <div class="login-stage">
          <cpl-a2k-window
            class="login-window ${this.draggingWindow ? "login-window-dragging" : ""}"
            style=${styleMap({ transform: `translate(${this.windowX}px, ${this.windowY}px)` })}
            .active=${this.activeSurface === "login" && !this.registerOpen}
            @focusin=${() => {
              this.activeSurface = "login";
            }}
            @pointerdown=${(event: PointerEvent) => {
              this.activeSurface = "login";
              this.beginWindowDrag(event);
            }}
            @pointermove=${this.moveWindow}
            @pointerup=${this.endWindowDrag}
            @pointercancel=${this.endWindowDrag}
            @lostpointercapture=${this.endWindowDrag}
            @dblclick=${(event: MouseEvent) => {
              if (this.onTitle(event)) this.resetWindow();
            }}
            heading=${i18n.t("loginHeading")}
            layout="flow"
          >
            <form
              class="login-form"
              method="post"
              action="/api/auth/login"
              autocomplete="on"
              @submit=${this.submit}
            >
              <div class="login-logo">
                <span class="brand-mark">CJ</span>
                <div>
                  <h1>${i18n.t("loginTitle")}</h1>
                  <p>${i18n.t("loginDescription")}</p>
                </div>
              </div>
              <label class="native-field">
                ${i18n.t("loginUsername")}
                <input
                  class="a2k-native-input"
                  name="username"
                  type="text"
                  autocomplete="username"
                  required
                />
              </label>
              <label class="native-field">
                ${i18n.t("loginPassword")}
                <input
                  class="a2k-native-input"
                  name="password"
                  type="password"
                  autocomplete="current-password"
                  required
                />
              </label>
              <button class="login-submit" type="submit" ?disabled=${this.busy}>
                ${this.busy ? i18n.t("working") : i18n.t("loginSubmit")}
              </button>
              <a2k-button
                type="button"
                ?disabled=${this.setupLoading}
                @click=${this.openRegister}
              >
                ${i18n.t(this.setupRequired ? "setupTitle" : "registerAction")}
              </a2k-button>
              ${this.registerSuccess ? html`<p class="status status-success">${this.registerSuccess}</p>` : null}
              ${
                this.error
                  ? html`<p class="status status-error" role="alert">
                      ${this.error}
                    </p>`
                  : null
              }
              <a class="login-docs" href="/api-docs">
                ${i18n.t("loginDocs")}
              </a>
            </form>
          </cpl-a2k-window>
        </div>
      </a2k-desktop>
      <app-dialog
        .open=${this.registerOpen}
        heading=${i18n.t(this.setupRequired ? "setupTitle" : "registerTitle")}
        confirm-label=${i18n.t("registerAction")}
        cancel-label=${i18n.t("cancel")}
        working-label=${i18n.t("working")}
        .busy=${this.registerBusy}
        .message=${this.registerError}
        @dialog-cancel=${() => (this.registerOpen = false)}
        @dialog-confirm=${this.confirmRegister}
      >
        <form class="register-form" autocomplete="off" @submit=${this.register}>
          ${
            this.setupRequired
              ? html`
                  <p>${i18n.t("setupHelp")}</p>
                  <code>.tmp/admin-bootstrap/registration-code</code>
                  <label class="native-field">
                    ${i18n.t("setupCode")}
                    <input
                      class="a2k-native-input"
                      name="setup_code"
                      type="password"
                      autocomplete="off"
                      required
                    />
                  </label>
                `
              : null
          }
          <label class="native-field"
            >${i18n.t("registerEmail")}<input
              class="a2k-native-input"
              name="email"
              type="email"
              autocomplete="off"
              required
          /></label>
          <label class="native-field"
            >${i18n.t("loginPassword")}<input
              class="a2k-native-input"
              name="password"
              type="password"
              autocomplete="new-password"
              minlength="8"
              required
          /></label>
          <label class="native-field"
            >${i18n.t("registerConfirmPassword")}<input
              class="a2k-native-input"
              name="password_confirm"
              type="password"
              autocomplete="new-password"
              minlength="8"
              required
          /></label>
        </form>
      </app-dialog>
    `;
  }
}
